import { Button, TextField } from '@cfa/react-components';
import { Key, Select } from '@cfa/react-core';
import { notifyBugsnag } from 'cfa-react-logging';
import { groupBy } from 'ramda';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { v4 } from 'uuid';

import Disclaimer from '../../../components/Disclaimer';
import { selectIndexedMenuItems } from '../../../menu/selectors';
import { isVcaUser } from '../../../user/selectors';
import { debounce } from '../../../util/utils';
import { actions as cartActions } from '../../reducer';
import { mapWithQuantityForToggleableModifiers } from '../../utils';
import Modifier from '../Modifier';
import ModifierList from '../ModifierList';
import NoModifiers from '../NoModifiers';
import PromoFreeToggle from '../PromoFreeToggle';

interface Props {
  toggleEditMode: () => void;
  cartItem: CartItemView;
}

const EditCartItem: React.FC<Props> = ({ cartItem, toggleEditMode }) => {
  const {
    tag,
    quantity,
    sideItem,
    premiumSideItem,
    dessertItem,
    id,
    otherItems,
    promoFree,
    specialInstructions = '',
  } = cartItem;

  const dispatch = useDispatch();
  const isVca = useSelector(isVcaUser);
  const menuItems = useSelector(selectIndexedMenuItems);
  const [isToggledPromoFree, setTogglePromoFree] = useState(promoFree);
  const update = debounce((text: string) => {
    dispatch(cartActions.updateSpecialInstructions({ id, specialInstructions: text }));
  }, 300);
  const { dessertItems = [], sideItems = [], premiumSideItems = [], freeSauces = 0 } = menuItems[tag];
  const sidesObj = groupBy((item) => item.tag, sideItems);
  const premiumSidesObj = groupBy((item) => item.tag, premiumSideItems);
  const dessertsObj = groupBy((item) => item.tag, dessertItems);
  const modifiersObj = groupBy((item) => item.grouping ?? 'Other', otherItems);
  const selectedItems = useMemo(
    () =>
      otherItems
        .concat(dessertItem ?? [], sideItem ?? [], premiumSideItem ?? [])
        .map(mapWithQuantityForToggleableModifiers)
        .filter((item) => item.quantity > 0),
    [otherItems, dessertItem, sideItem, premiumSideItem],
  );

  const togglePromoFreeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked === true) {
      setTogglePromoFree(!isToggledPromoFree);
      dispatch(cartActions.makePromoFree({ id }));
    } else {
      setTogglePromoFree(!isToggledPromoFree);
      dispatch(cartActions.removePromoFree({ id }));
    }
  };

  const handleSideItemChange = (itemKey: Key | null) => {
    if (itemKey && sidesObj && sidesObj[itemKey]) {
      dispatch(cartActions.updateSideItem({ id, selectedSideTag: sidesObj[itemKey][0].tag }));
    } else {
      notifyBugsnag({
        grouping: 'Edit Side Item Failed',
        message: 'Edit Cart Item',
        error: {
          error: 'Matching item key not found',
          sides: sidesObj,
          item: tag,
          eventTargetValue: itemKey ?? 'undefined or null',
        },
      });
    }
  };

  const handlePremiumSideItemChange = (itemKey: Key | null) => {
    if (itemKey && premiumSidesObj && premiumSidesObj[itemKey]) {
      dispatch(
        cartActions.updatePremiumSideItem({ id, selectedPremiumSideTag: premiumSidesObj[itemKey][0].tag }),
      );
    } else {
      notifyBugsnag({
        grouping: 'Edit Premium Side Item Failed',
        message: 'Edit Cart Item',
        error: {
          error: 'Matching item key not found',
          sides: premiumSidesObj,
          item: tag,
          eventTargetValue: itemKey ?? 'undefined or null',
        },
      });
    }
  };

  const handleDessertItemChange = (dessertKey: Key | null) => {
    if (dessertKey && dessertsObj && dessertsObj[dessertKey]) {
      dispatch(cartActions.updateDessertItem({ id, selectedDessertTag: dessertsObj[dessertKey][0].tag }));
    } else {
      notifyBugsnag({
        grouping: 'Edit Dessert Item Failed',
        message: 'Edit Cart Item',
        error: {
          error: 'Matching dessert key not found',
          desserts: dessertsObj,
          item: tag,
          eventTargetValue: dessertKey ?? 'undefined or null',
        },
      });
    }
  };

  const handleAddAnotherClicked = () => {
    dispatch(
      cartActions.addToCart({
        id: v4(),
        itemTag: tag,
        dessertTag: dessertItem?.tag,
        sideTag: sideItem?.tag,
        premiumSideTag: premiumSideItem?.tag,
      }),
    );
    toggleEditMode();
  };

  const getQuantityOfModifierType = useCallback(
    (modifierType: string) => {
      return modifiersObj[modifierType]?.map((item) => item.quantity ?? 0).reduce((a, b) => a + b, 0);
    },
    [modifiersObj],
  );

  return (
    <StyledEditCartItem data-testid="cart-item-edit-box">
      <div className="edit-container">
        {sideItems.length > 0 && (
          <Select.Root
            className="modifier-select"
            data-cy={`${tag}-side`}
            data-testid={`${tag}-side`}
            onChange={handleSideItemChange}
            value={sideItem?.tag}
          >
            <Select.Label>Side</Select.Label>
            <Select.Button>
              <Select.Value />
            </Select.Button>
            <Select.Popover>
              <Select.List items={sideItems}>
                {(side) => <Select.Item id={side.tag}>{side.name}</Select.Item>}
              </Select.List>
            </Select.Popover>
          </Select.Root>
        )}
        {premiumSideItems.length > 0 && (
          <Select.Root
            className="modifier-select"
            data-cy={`${tag}-premium-side`}
            data-testid={`${tag}-premium-side`}
            onChange={handlePremiumSideItemChange}
            value={premiumSideItem?.tag}
          >
            <Select.Label>Premium Side</Select.Label>
            <Select.Button>
              <Select.Value />
            </Select.Button>
            <Select.Popover>
              <Select.List items={premiumSideItems}>
                {(premiumSide) => <Select.Item id={premiumSide.tag}>{premiumSide.name}</Select.Item>}
              </Select.List>
            </Select.Popover>
          </Select.Root>
        )}
        {dessertItems.length > 0 && (
          <Select.Root
            className="modifier-select"
            data-cy={`${tag}-dessert`}
            data-testid={`${tag}-dessert`}
            onChange={handleDessertItemChange}
            value={dessertItem?.tag}
          >
            <Select.Label>Treat</Select.Label>
            <Select.Button>
              <Select.Value />
            </Select.Button>
            <Select.Popover>
              <Select.List items={dessertItems}>
                {(dessert) => <Select.Item id={dessert.tag}>{dessert.name}</Select.Item>}
              </Select.List>
            </Select.Popover>
          </Select.Root>
        )}
        <div className="includes-label">Includes:</div>
        {selectedItems.length === 0 && <NoModifiers />}
        {selectedItems.length > 0 && <ModifierList modifiers={selectedItems} />}
        {freeSauces > 0 && (
          <div>
            <Disclaimer className="sauce-disclaimer">{`${freeSauces * quantity} Free Sauce(s)`}</Disclaimer>
          </div>
        )}
        {Object.keys(modifiersObj)
          .filter((modifierType) => modifierType !== 'Other')
          .map((modifierType) => (
            <div key={modifierType}>
              <div className="modifier-type">{modifierType}:</div>
              <div className="modifier-container">
                {modifiersObj[modifierType]
                  ?.filter((subItem) => subItem.tag !== 'CARAMEL')
                  .map((mod) => (
                    <Modifier
                      cartItemId={id}
                      item={mod}
                      key={mod.tag}
                      maximum={mod.maximum ? mod.maximum * quantity : 999}
                      modifierGroupingCount={getQuantityOfModifierType(modifierType)}
                    />
                  ))}
              </div>
            </div>
          ))}
        {modifiersObj.Other && modifiersObj.Other.length > 0 && (
          <div>
            <div className="modifier-type">Other:</div>
            <div className="modifier-container">
              {modifiersObj.Other.filter((subItem) => subItem.tag !== 'CARAMEL').map((mod) => (
                <Modifier cartItemId={id} item={mod} key={mod.tag} />
              ))}
            </div>
          </div>
        )}
        {!isVca && (
          <div>
            <PromoFreeToggle
              id={id}
              isToggledPromoFree={isToggledPromoFree}
              togglePromoFreeHandler={togglePromoFreeHandler}
            />
          </div>
        )}
        <div>
          <TextField
            className="special-instructions"
            defaultValue={specialInstructions}
            maxLength={40}
            multiline
            onChange={(e) => update(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Special Instructions"
          />
        </div>
        <div>
          <ButtonContainer>
            <Button
              className="add-another"
              color="secondary"
              data-cy={`split-${tag}`}
              fullWidth
              onClick={handleAddAnotherClicked}
              variant="outlined"
            >
              Add Another & Customize
            </Button>
            <Button
              className="delete-item"
              data-cy={`delete-${tag}`}
              fullWidth
              onClick={() => dispatch(cartActions.deleteItem({ id }))}
              variant="destructive"
            >
              Delete Item
            </Button>
          </ButtonContainer>
        </div>
      </div>
    </StyledEditCartItem>
  );
};

const StyledEditCartItem = styled.div`
  margin-top: 20px;

  & .edit-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  & .modifier-container {
    display: flex;
    justify-content: flex-start;
    flex-flow: column wrap;
  }

  & .modifier-type {
    width: 100%;
    text-align: left;
    font: ${(props) => props.theme.smallBoldFont};
    margin-bottom: 8px;
  }

  & .includes-label {
    font: ${(props) => props.theme.smallBoldFont};
  }

  & .sauce-disclaimer {
    text-align: left;
    margin-left: 0;
  }

  & .special-instructions {
    height: 60px;
  }

  & .promo-free-box {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-right: 10px;
  }

  & .modifier-select {
    padding-right: 10px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;

  @media (max-width: ${(props) => props.theme.phone}) {
    flex-direction: column;
  }

  & button {
    margin: 8px;
  }
`;

export default EditCartItem;
